import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiX, FiClock, FiEye, FiCalendar, FiMapPin, FiShare2, FiUser } = FiIcons;

const formatDate = (dateStr) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};

// ── Video embed renderer ─────────────────────────────────────────────────────
const VideoEmbed = ({ replay }) => {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => setIsLoading(false), 1200);
    return () => clearTimeout(timer);
  }, [replay.id]);

  const renderContent = () => {
    switch (replay.platform) {
      case 'youtube':
        return replay.embedId ? (
          <iframe
            className="w-full h-full"
            src={`https://www.youtube.com/embed/${replay.embedId}?autoplay=1&mute=0`}
            title={replay.title}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <div className="flex items-center justify-center h-full bg-accent-navy text-white/60 text-sm">
            Recording unavailable
          </div>
        );
      case 'facebook':
        return replay.streamUrl ? (
          <iframe
            src={`https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(replay.streamUrl)}&show_text=0&width=560`}
            className="w-full h-full"
            style={{ border: 'none', overflow: 'hidden' }}
            scrolling="no"
            frameBorder="0"
            allowFullScreen
            allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
          />
        ) : (
          <div className="flex items-center justify-center h-full bg-accent-navy text-white/60 text-sm">
            Recording unavailable
          </div>
        );
      default:
        return (
          <div className="flex flex-col items-center justify-center h-full bg-gradient-to-br from-accent-navy to-slate-900 p-8 text-center text-white">
            <SafeIcon icon={FiUser} className="w-16 h-16 text-accent-gold mb-6" />
            <h3 className="text-2xl font-serif mb-3">{replay.title}</h3>
            <p className="text-white/50 max-w-sm text-sm mb-8">
              This private sanctuary recording is available to authorised family members and community members.
            </p>
            <div className="inline-flex items-center gap-3 text-white/40 text-[10px] font-black uppercase tracking-widest bg-white/5 px-6 py-3 rounded-full border border-white/10">
              <SafeIcon icon={FiClock} className="text-accent-gold" />
              Recording Length: {replay.duration}
            </div>
          </div>
        );
    }
  };

  return (
    <div className="relative w-full h-full bg-black overflow-hidden">
      <AnimatePresence mode="wait">
        {isLoading ? (
          <motion.div
            key="loader"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 z-50 bg-accent-navy flex flex-col items-center justify-center"
          >
            <div className="w-14 h-14 border-4 border-accent-gold/20 border-t-accent-gold rounded-full animate-spin mb-4" />
            <p className="text-accent-gold text-[10px] font-black uppercase tracking-widest">Loading Recording</p>
          </motion.div>
        ) : (
          <motion.div
            key="content"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="w-full h-full"
          >
            {renderContent()}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ── Modal ────────────────────────────────────────────────────────────────────
const ReplayPlayerModal = ({ replay, onClose }) => {
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEsc);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  if (!replay) return null;

  const handleShare = () => {
    const url = `${window.location.origin}/#/replays`;
    if (navigator.share) {
      navigator.share({ title: replay.title, url }).catch(() => {});
    } else {
      navigator.clipboard.writeText(url).catch(() => {});
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-0 sm:p-4 bg-black/80 backdrop-blur-md">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="bg-accent-navy w-full h-full sm:h-auto sm:max-w-5xl sm:rounded-[32px] shadow-2xl overflow-hidden flex flex-col"
      >
        {/* Header bar */}
        <div className="flex items-center justify-between p-4 sm:p-5 bg-black/40 backdrop-blur-xl border-b border-white/10 flex-shrink-0">
          <div className="min-w-0 flex-1 pr-4">
            <h2 className="text-white font-serif text-base sm:text-lg line-clamp-1">{replay.title}</h2>
            <p className="text-white/40 text-[10px] font-bold uppercase tracking-widest mt-0.5">
              Hosted by {replay.organizer}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={handleShare}
              className="p-3 bg-white/5 text-white/60 rounded-xl hover:bg-white/10 hover:text-white transition-all border border-white/10"
              title="Share recording"
            >
              <SafeIcon icon={FiShare2} className="w-4 h-4" />
            </button>
            <button
              onClick={onClose}
              className="p-3 bg-white/5 text-white/60 rounded-xl hover:bg-red-500/20 hover:text-red-400 transition-all border border-white/10"
              title="Close"
            >
              <SafeIcon icon={FiX} className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Video player */}
        <div className="relative aspect-video bg-black flex-shrink-0">
          <VideoEmbed replay={replay} />
        </div>

        {/* Info bar */}
        <div className="p-5 sm:p-6 bg-accent-navy flex flex-col sm:flex-row gap-4 sm:items-center justify-between flex-shrink-0">
          <div className="flex flex-wrap items-center gap-4 text-white/50 text-[10px] font-bold">
            <span className="flex items-center gap-1.5">
              <SafeIcon icon={FiClock} className="w-3.5 h-3.5 text-accent-gold" />
              {replay.duration}
            </span>
            <span className="flex items-center gap-1.5">
              <SafeIcon icon={FiEye} className="w-3.5 h-3.5 text-accent-gold" />
              {replay.views.toLocaleString()} views
            </span>
            <span className="flex items-center gap-1.5">
              <SafeIcon icon={FiCalendar} className="w-3.5 h-3.5 text-accent-gold" />
              {formatDate(replay.recordedDate)}
            </span>
            {replay.location && (
              <span className="flex items-center gap-1.5">
                <SafeIcon icon={FiMapPin} className="w-3.5 h-3.5 text-accent-gold" />
                {replay.location}
              </span>
            )}
          </div>
          <div className="flex-shrink-0">
            <span className="px-3 py-1.5 bg-white/5 text-white/60 rounded-full text-[9px] font-black uppercase tracking-widest border border-white/10">
              {replay.category}
            </span>
          </div>
        </div>

        {/* Description */}
        {replay.description && (
          <div className="px-5 sm:px-6 pb-6 bg-accent-navy flex-shrink-0">
            <p className="text-white/40 text-xs leading-relaxed font-light">
              {replay.description}
            </p>
          </div>
        )}
      </motion.div>
    </div>,
    document.body
  );
};

export default ReplayPlayerModal;